import { SOURCE_POLICIES } from "../network/SourceRegistry.ts";
import { hasDecoder } from "./decoders/registry.ts";
import { resolveUrl } from "./UrlResolver.ts";
import { ExtractionError, type DecoderRule, type SourceProfile } from "./types.ts";

const fail = (message: string): never => {
  throw new ExtractionError("INVALID_PROFILE", message);
};
function object(value: unknown, keys?: string[]): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return fail("Se esperaba un objeto.");
  if (keys && Object.keys(value).some((key) => !keys.includes(key)))
    fail("Propiedad no admitida en el perfil.");
  if (Object.keys(value).some((key) => ["__proto__", "prototype", "constructor"].includes(key)))
    fail("Propiedad prohibida.");
  return value as Record<string, unknown>;
}
function text(value: unknown): asserts value is string {
  if (typeof value !== "string" || !value || value.length > 8192) fail("Texto inválido.");
}
function optionalBoolean(value: unknown) {
  if (value !== undefined && typeof value !== "boolean") fail("Booleano inválido.");
}
function decoder(value: unknown) {
  if (value === undefined) return;
  const rule = object(value, ["kind", "name"]);
  if (
    rule.kind !== "builtin" ||
    typeof rule.name !== "string" ||
    !hasDecoder(rule as unknown as DecoderRule)
  )
    fail("Decoder no registrado.");
}
function field(value: unknown) {
  const rule = object(value, [
    "all",
    "asset",
    "label",
    "valueSelector",
    "selector",
    "path",
    "attributes",
    "transforms",
    "decoder",
    "required",
  ]);
  for (const key of ["selector", "path", "label", "valueSelector"])
    if (rule[key] !== undefined) {
      text(rule[key]);
      if (
        key === "path" &&
        /(?:^|\.)(__proto__|constructor|prototype)(?:\.|$)/.test(rule[key] as string)
      )
        fail("Ruta prohibida.");
    }
  if (
    rule.attributes !== undefined &&
    (!Array.isArray(rule.attributes) ||
      !rule.attributes.length ||
      rule.attributes.some((a) => typeof a !== "string" || !/^[\w:-]+$/.test(a)))
  )
    fail("Atributos inválidos.");
  if (
    rule.transforms !== undefined &&
    (!Array.isArray(rule.transforms) ||
      rule.transforms.some((t) => !["trim", "absolute-url", "number", "srcset"].includes(t)))
  )
    fail("Transformación desconocida.");
  optionalBoolean(rule.required);
  optionalBoolean(rule.all);
  optionalBoolean(rule.asset);
  if (rule.label !== undefined && !rule.selector) fail("label requiere selector.");
  if (rule.valueSelector !== undefined && !rule.label) fail("valueSelector requiere label.");
  decoder(rule.decoder);
}
function fields(value: unknown) {
  const map = object(value);
  if (!Object.keys(map).length || Object.keys(map).length > 64)
    fail("Mapa de campos vacío o demasiado grande.");
  Object.values(map).forEach(field);
}
function variables(value: string) {
  const masked = value.replace(/\{([^{}]+)\}/g, (_, key: string) => {
    if (
      !/^(baseUrl|query|page|manga\.(id|url)|chapter\.(id|url)|prev\.[A-Za-z][\w]*(?:\.[A-Za-z0-9_]+)*)$/.test(
        key,
      ) ||
      key.split(".").some((part) => ["__proto__", "constructor", "prototype"].includes(part))
    )
      fail(`Variable no admitida: ${key}`);
    return "";
  });
  if (/[{}]/.test(masked)) fail("Interpolación inválida.");
}
function templateUrl(value: unknown, profile: SourceProfile) {
  text(value);
  variables(value);
  // The authority cannot be built from user input. Dynamic full URLs are checked again at execution.
  const masked = value.replace(/\{([^{}]+)\}/g, (_, key: string) => {
    if (
      !/^(baseUrl|query|page|manga\.(id|url)|chapter\.(id|url)|prev\.[A-Za-z][\w]*(?:\.[A-Za-z0-9_]+)*)$/.test(
        key,
      )
    )
      fail(`Variable no admitida: ${key}`);
    return key === "baseUrl" || key.endsWith(".url") ? profile.baseUrl : "value";
  });
  if (/[{}]/.test(masked)) fail("Interpolación inválida.");
  resolveUrl(masked, profile.baseUrl, profile);
}
export function validateProfile(input: unknown): SourceProfile {
  const raw = object(input, [
    "schemaVersion",
    "id",
    "name",
    "baseUrl",
    "allowedHosts",
    "template",
    "corsOpen",
    "requiresSession",
    "cacheTtlMs",
    "operations",
    "catalog",
    "latest",
    "ui",
  ]);
  if (raw.schemaVersion !== 1) fail("schemaVersion debe ser 1.");
  text(raw.id);
  if (
    !/^[a-z0-9][a-z0-9._-]*$/i.test(raw.id) ||
    ["__proto__", "constructor", "prototype"].includes(raw.id)
  )
    fail("Identificador de fuente inválido.");
  text(raw.name);
  text(raw.baseUrl);
  if (
    !Array.isArray(raw.allowedHosts) ||
    !raw.allowedHosts.length ||
    raw.allowedHosts.length > 64 ||
    raw.allowedHosts.some(
      (host) => typeof host !== "string" || !/^(?:\*\.)?[a-z0-9]+(?:[.-][a-z0-9]+)*$/.test(host),
    )
  )
    fail("allowedHosts inválido.");
  const hosts = raw.allowedHosts as string[];
  if (new Set(hosts).size !== hosts.length) fail("allowedHosts contiene duplicados.");
  if (
    !["Madara", "MangaThemesia", "TMO", "LeerCapitulo", "CustomCSS"].includes(String(raw.template))
  )
    fail("Plantilla desconocida.");
  optionalBoolean(raw.corsOpen);
  optionalBoolean(raw.requiresSession);
  if (
    raw.cacheTtlMs !== undefined &&
    (typeof raw.cacheTtlMs !== "number" ||
      !Number.isFinite(raw.cacheTtlMs) ||
      raw.cacheTtlMs < 0 ||
      raw.cacheTtlMs > 3600000)
  )
    fail("TTL inválido.");
  const profile = raw as unknown as SourceProfile;
  // A known source can narrow its hosts, never widen the trusted Bridge catalog.
  const policy = Object.hasOwn(SOURCE_POLICIES, profile.id)
    ? SOURCE_POLICIES[profile.id]
    : undefined;
  if (policy && profile.allowedHosts.some((host) => !policy.allowedHosts.includes(host)))
    fail("allowedHosts excede SourceRegistry.");
  resolveUrl(profile.baseUrl, profile.baseUrl, profile);
  if (raw.ui !== undefined) {
    const ui = object(raw.ui, [
      "iconUrl",
      "mangaPath",
      "chapterPath",
      "idMode",
      "languages",
      "trailingSlash",
      "coverPath",
      "paginationParams",
      "latestOrder",
      "chapterDelivery",
      "pageSize",
    ]);
    optionalBoolean(ui.trailingSlash);
    if (ui.chapterDelivery !== undefined && ui.chapterDelivery !== 'external-file') fail('chapterDelivery inválido.');
    if (ui.pageSize !== undefined && (!Number.isInteger(ui.pageSize) || Number(ui.pageSize) < 1 || Number(ui.pageSize) > 100)) fail('pageSize inválido.');
    if (ui.coverPath !== undefined) templateUrl(ui.coverPath, profile);
    if (ui.paginationParams !== undefined) Object.values(object(ui.paginationParams)).forEach(text);
    if (ui.latestOrder !== undefined && !["added", "updated"].includes(String(ui.latestOrder))) fail("latestOrder inválido.");
    text(ui.mangaPath);
    text(ui.chapterPath);
    resolveUrl(String(ui.mangaPath), profile.baseUrl, profile);
    resolveUrl(String(ui.chapterPath), profile.baseUrl, profile);
    if (!["path", "url"].includes(String(ui.idMode))) fail("idMode inválido.");
    if (ui.iconUrl !== undefined) {
      text(ui.iconUrl);
      if (!String(ui.iconUrl).startsWith("/source-icons/"))
        resolveUrl(String(ui.iconUrl), profile.baseUrl, profile);
    }
    if (
      ui.languages !== undefined &&
      (!Array.isArray(ui.languages) || ui.languages.some((v) => typeof v !== "string"))
    )
      fail("Idiomas inválidos.");
  }
  const operations = object(raw.operations, ["search", "detail", "chapters", "pages"]);
  for (const value of [
    ...["search", "detail", "chapters", "pages"].map((key) => operations[key]),
    ...[raw.catalog, raw.latest].filter((v) => v !== undefined),
  ]) {
    const definition = object(value, ["steps", "fallbacks"]);
    if (
      definition.fallbacks !== undefined &&
      (!Array.isArray(definition.fallbacks) || definition.fallbacks.length > 3)
    )
      fail("Fallbacks inválidos.");
    for (const variant of [
      { steps: definition.steps },
      ...((definition.fallbacks ?? []) as unknown[]),
    ]) {
      const config = object(variant, ["steps"]);
      if (!Array.isArray(config.steps) || !config.steps.length || config.steps.length > 16)
        fail("Cada operación requiere entre 1 y 16 steps.");
      for (const value of config.steps as unknown[]) {
        const step = object(value, [
          "url",
          "method",
          "referer",
          "headers",
          "form",
          "capture",
          "extract",
        ]);
        templateUrl(step.url, profile);
        if (step.referer !== undefined) templateUrl(step.referer, profile);
        if (step.method !== undefined && !["GET", "POST"].includes(String(step.method)))
          fail("Método inválido.");
        if (step.form !== undefined && step.method !== "POST") fail("form requiere POST.");
        for (const kind of ["headers", "form"])
          if (step[kind] !== undefined) {
            for (const [key, value] of Object.entries(object(step[kind]))) {
              text(value);
              variables(value);
              if (
                kind === "headers" &&
                (!/^[!#$%&'*+.^_`|~\w-]+$/.test(key) || /\r|\n/.test(value))
              )
                fail("Cabecera inválida.");
              if (kind === "headers" && /^(referer|origin)$/i.test(key))
                templateUrl(value, profile);
            }
          }
        if (step.capture !== undefined) fields(step.capture);
        if (step.extract !== undefined) {
          const extract = object(step.extract, [
            "kind",
            "selector",
            "path",
            "fields",
            "emptySelector",
            "allowEmpty",
            "decoder",
          ]);
          if (!["html", "json"].includes(String(extract.kind))) fail("Extractor inválido.");
          for (const key of ["selector", "path", "emptySelector"])
            if (extract[key] !== undefined) text(extract[key]);
          if (
            typeof extract.path === "string" &&
            extract.path
              .split(".")
              .some((part) => ["__proto__", "constructor", "prototype"].includes(part))
          )
            fail("Ruta JSON prohibida.");
          fields(extract.fields);
          optionalBoolean(extract.allowEmpty);
          decoder(extract.decoder);
          if (extract.decoder && extract.kind !== "json")
            fail("El decoder de extracción devuelve JSON.");
        }
      }
      const steps = config.steps as unknown[];
      if (!object(steps[steps.length - 1]).extract) fail("El último step requiere extract.");
    }
  }
  // Snapshot prevents mutation after validation (and strips exotic prototypes).
  return JSON.parse(JSON.stringify(profile)) as SourceProfile;
}
